import {
  STORM_SEARCH_DEFAULTS,
  STORM_SEARCH_LIMITS,
  multiReportDays,
  primaryDateOfLoss,
  renderStormReportHtml,
  validateStormSearch,
  type Job,
  type StormEvent,
  type StormReportSnapshot,
  type StormSearch,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t, tn } from "../../i18n";
import { fmtDateTime } from "../../lib/format";
import { sendEmail } from "../../lib/gmail";
import { useAsync } from "../../lib/hooks";
import {
  fetchStormReports,
  markStormReportSent,
  pullStormReport,
  saveStormReport,
  stormReportBlocker,
  stormReportUrl,
  voidStormReport,
  type StormReportRow,
} from "../../lib/stormReport";
import { useSession } from "../../session";

/**
 * Storm history, on the job it belongs to.
 *
 * One button pulls every hail report the National Weather Service holds for the
 * neighbourhood and turns it into a document an adjuster will read — unbranded
 * on purpose, with a citation beside every row. See ADR 0043.
 *
 * Two decisions in here are worth knowing about before changing them.
 *
 * **Pulling and keeping are separate steps.** The pull shows what came back and
 * waits. That is not caution for its own sake: a day that produced four or more
 * reports years before the roof went on is prior exposure, and prior exposure
 * argues about roof age rather than about this storm. It can cut against the
 * claim. So it is put in front of the rep, in words, before the document exists
 * as something they can send.
 *
 * **A saved report is never rewritten.** A database trigger refuses. Local Storm
 * Reports are preliminary and get revised in quality control, so re-running the
 * query is not a way of reproducing what was sent to a carrier — which makes the
 * stored copy the only answer to "what did you send my adjuster in March?".
 */
export function StormReportPanel({ job }: { job: Job }) {
  const reports = useAsync(() => fetchStormReports(job.id), [job.id]);
  const [pulling, setPulling] = useState(false);
  const [emailing, setEmailing] = useState<StormReportRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const blocker = stormReportBlocker(job);
  const rows = reports.data ?? [];

  async function voidReport(id: string) {
    try {
      await voidStormReport(id);
      await reports.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <div className="block">
      <h2>{t("Storm history")}</h2>

      <ErrorNote error={error ?? reports.error} />

      {blocker ? (
        <div className="stub">
          <b>{t("No address on this job")}</b>
          {t(blocker)}
        </div>
      ) : (
        <p className="na-hint" style={{ marginTop: 0 }}>
          {t(
            "Every hail and wind report the National Weather Service holds near {address}, written up with a link beside each one so an adjuster can check it. Nothing on it names us.",
            { address: job.address ?? "" },
          )}
        </p>
      )}

      {/* Not `.actions` — that is the modal footer, where buttons are flex:1
          and stretch to fill the row. */}
      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          className="btn-primary"
          style={{ padding: "10px 18px", borderRadius: 8, border: 0 }}
          disabled={Boolean(blocker)}
          onClick={() => {
            setError(null);
            setPulling(true);
          }}
        >
          {t("Pull Storm Report")}
        </button>
      </div>

      {rows.length > 0 && (
        <div className="ewrap" style={{ marginTop: 12 }}>
          <table className="etable">
            <thead>
              <tr>
                <th>{t("Pulled")}</th>
                <th>{t("Searched")}</th>
                <th>{t("Found")}</th>
                <th>{t("State")}</th>
                <th>{t("Adjuster opened")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <StormRow
                  key={row.id}
                  row={row}
                  onEmail={() => setEmailing(row)}
                  // Fire and forget: voidReport() shows its own errors.
                  onVoid={() => void voidReport(row.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pulling && (
        <PullModal
          job={job}
          onClose={() => setPulling(false)}
          onSaved={() => {
            setPulling(false);
            // Fire and forget: reload() puts its own error in reports.error.
            void reports.reload();
          }}
        />
      )}

      {emailing && (
        <EmailModal
          job={job}
          row={emailing}
          onClose={() => setEmailing(null)}
          onSent={() => {
            setEmailing(null);
            // Fire and forget: reload() puts its own error in reports.error.
            void reports.reload();
          }}
        />
      )}
    </div>
  );
}

const STATE_LABEL: Record<StormReportRow["status"], string> = {
  ready: "Pulled, not sent",
  sent: "Sent",
  void: "Link killed",
};

function StormRow({
  row,
  onEmail,
  onVoid,
}: {
  row: StormReportRow;
  onEmail: () => void;
  onVoid: () => void;
}) {
  const dead = row.status === "void";
  return (
    <tr>
      <td className="sub">{fmtDateTime(row.created_at)}</td>
      <td className="sub">
        {t('{years} yr · {inches}" hail {miles} mi', {
          years: row.years,
          inches: row.threshold_inches,
          miles: row.radius_miles,
        })}
        {row.wind_radius_miles === null ? (
          // Pulled before the report covered wind at all. Saying "no wind
          // found" about it would be a claim the report never made.
          <span className="na-hint"> · {t("no wind")}</span>
        ) : (
          <> · {t("wind {miles} mi", { miles: row.wind_radius_miles })}</>
        )}
      </td>
      <td>
        {row.event_count === 0
          ? t("Nothing")
          : tn(row.event_count, "1 report", "{n} reports")}
      </td>
      <td>
        <span className={`docstate ${row.status}`}>
          {t(STATE_LABEL[row.status])}
        </span>
        {row.sent_at && (
          <span className="na-hint"> · {fmtDateTime(row.sent_at)}</span>
        )}
      </td>
      <td className="sub">
        {/* Whether the adjuster actually looked. Recorded by the Edge Function
            that serves the link, because they hold a token and not a login. */}
        {row.opened_at
          ? `${fmtDateTime(row.opened_at)}${row.open_count > 1 ? ` · ${t("{n} times", { n: row.open_count })}` : ""}`
          : "—"}
      </td>
      <td>
        {!dead && (
          <>
            <a
              className="clink"
              href={stormReportUrl(row.token)}
              target="_blank"
              rel="noreferrer"
            >
              {t("Open ↗")}
            </a>{" "}
            <span className="clink" onClick={onEmail}>
              {t("Email")}
            </span>{" "}
            <span
              className="clink"
              onClick={onVoid}
              title={t("Kill the link. The record of what was sent is kept.")}
            >
              {t("Kill link")}
            </span>
          </>
        )}
      </td>
    </tr>
  );
}

/** The three numbers a rep may change, and what came back when they did. */
function PullModal({
  job,
  onClose,
  onSaved,
}: {
  job: Job;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { profile } = useSession();
  const [search, setSearch] = useState<StormSearch>(STORM_SEARCH_DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<StormReportSnapshot | null>(null);

  const problems = validateStormSearch(search);

  const field = (
    key: keyof StormSearch,
    label: string,
    step: string,
    suffix: string,
  ) => (
    <div className="field" key={key}>
      <label>
        {label}
        <span className="na-hint" style={{ marginLeft: 6 }}>
          {suffix}
        </span>
      </label>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        min={STORM_SEARCH_LIMITS[key].min}
        max={STORM_SEARCH_LIMITS[key].max}
        value={String(search[key])}
        disabled={busy || Boolean(result)}
        onChange={(e) =>
          setSearch({ ...search, [key]: Number(e.target.value) })
        }
      />
    </div>
  );

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await pullStormReport(job, search));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  const keep = async () => {
    if (!result) return;
    setBusy(true);
    setError(null);
    try {
      await saveStormReport(job.id, result, profile?.id ?? null);
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t("Pull Storm Report")}
      subtitle={job.address ?? undefined}
      onClose={onClose}
      actions={
        result ? (
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={onClose}
              disabled={busy}
            >
              {t("Discard")}
            </button>
            <button
              type="button"
              className="btn-primary"
              // Fire and forget: keep() shows its own errors.
              onClick={() => void keep()}
              disabled={busy}
            >
              {busy ? t("Saving…") : t("Keep this report")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={onClose}
              disabled={busy}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="btn-primary"
              // Fire and forget: run() shows its own errors.
              onClick={() => void run()}
              disabled={busy || problems.length > 0}
            >
              {busy ? t("Searching…") : t("Search")}
            </button>
          </>
        )
      }
    >
      <ErrorNote error={error ?? problems[0]?.message ?? null} />

      <div className="fgrid">
        {field("years", t("Years back"), "1", t("how far back to look"))}
        {field(
          "thresholdInches",
          t("Hail size"),
          "0.25",
          t("inches and bigger"),
        )}
        {field(
          "radiusMiles",
          t("Hail: miles around"),
          "1",
          t("from the property"),
        )}
        {field(
          "windRadiusMiles",
          t("Wind: miles around"),
          "1",
          t("kept tighter"),
        )}
      </div>

      {!result && (
        <p className="na-hint">
          {t(
            "The defaults are ten years, five miles and three-quarter-inch hail. Widen it if a tight search comes back empty — whatever you use is printed on the report, so an adjuster can see the search was not tuned afterwards to produce a result.",
          )}
        </p>
      )}

      {result && <PullSummary snapshot={result} />}
    </Modal>
  );
}

/**
 * What came back, in plain words, before it becomes a document.
 *
 * The multi-report warning is the reason this step exists at all — see the note
 * at the top of the file.
 */
function PullSummary({ snapshot }: { snapshot: StormReportSnapshot }) {
  const primary = primaryDateOfLoss(snapshot.events);
  const heavy = multiReportDays(snapshot.events);
  const preview = () => {
    const page = window.open("", "_blank", "noopener");
    if (!page) return;
    page.document.write(renderStormReportHtml(snapshot));
    page.document.close();
  };

  if (!primary) {
    return (
      <div className="stub" style={{ marginTop: 12 }}>
        <b>{t("Nothing found")}</b>
        {t(
          'No hail at or above {inches}" was reported within {miles} miles in the last {years} years. That is a real answer, not a failure — hail bands are narrow and a report only exists where somebody called one in. You can widen the search, or keep this as a record that the search was done.',
          {
            inches: snapshot.search.thresholdInches,
            miles: snapshot.search.radiusMiles,
            years: snapshot.search.years,
          },
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div className="stub">
        <b>
          {tn(
            snapshot.counts.events,
            "1 report found · likely date of loss {day}",
            "{n} reports found · likely date of loss {day}",
            { day: primary.day },
          )}
        </b>
        {t(
          "The strongest one is {lead}, {miles} miles {bearing} of the house.",
          {
            lead: leadPhrase(primary.lead),
            miles: primary.lead.miles,
            bearing: primary.lead.bearing,
          },
        )}
      </div>

      {heavy.length > 0 && (
        <div className="stub" style={{ marginTop: 8 }}>
          <b>{t("Read this before you send it")}</b>
          {heavy.length === 1
            ? t("{day} produced {n} separate reports near this house.", {
                day: heavy[0]!.day,
                n: heavy[0]!.group.length,
              })
            : t(
                "{n} separate days produced four or more reports each near this house.",
                { n: heavy.length },
              )}{" "}
          {t(
            "If the roof was already on then, that is earlier hail the roof has already taken — useful context, but an adjuster can also use it to argue the roof is simply old. Your call whether it helps.",
          )}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <span className="clink" onClick={preview}>
          {t("Look at the report first ↗")}
        </span>
      </div>
    </div>
  );
}

/**
 * The lead report in a sentence a rep can read aloud.
 *
 * A wind damage report gets no invented number and no borrowed authority: it
 * says what the record says, which is that somebody reported damage.
 */
function leadPhrase(event: StormEvent): string {
  if (event.peril === "hail" && event.diameterInches !== null) {
    return t('{inches}" hail, {how}', {
      inches: event.diameterInches.toFixed(2),
      how:
        event.qualifier === "Measured"
          ? t(
              "physically measured by a National Weather Service employee — the best evidence this data holds",
            )
          : t("estimated by whoever reported it, not measured"),
    });
  }
  if (event.peril === "wind-gust" && event.gustMph !== null) {
    return t("a {mph} mph wind gust", { mph: Math.round(event.gustMph) });
  }
  return t("reported wind damage, with no wind speed recorded");
}

/** Send it to the adjuster: a link they can open and a copy they can keep. */
function EmailModal({
  job,
  row,
  onClose,
  onSent,
}: {
  job: Job;
  row: StormReportRow;
  onClose: () => void;
  onSent: () => void;
}) {
  const link = stormReportUrl(row.token);
  const address = job.address ?? "the property";

  const [to, setTo] = useState("");
  const [subject, setSubject] = useState(`Storm history — ${address}`);
  const [message, setMessage] = useState(
    `Attached is the storm history for ${address}, compiled from National Weather Service Local Storm Reports.\n\n` +
      `Every report listed carries a link to the public query that returns it, so any line can be checked independently.\n\n` +
      `It can also be read here:\n${link}\n`,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const html = renderStormReportHtml(row.snapshot);
      await sendEmail({
        to: to.trim(),
        subject: subject.trim(),
        text: message,
        jobId: job.id,
        contactId: job.contact_id ?? null,
        // An adjuster is not the customer, so this must not tick the
        // follow-up clock that counts contact with the homeowner.
        internal: true,
        attachments: [
          {
            filename: `storm-history-${job.job_number ?? "report"}.html`,
            mimeType: "text/html",
            // A web page rather than a PDF: nothing on the server can make a
            // PDF, and the HTML keeps the source URLs as selectable text
            // through a carrier's system, which a flattened PDF does not.
            content: base64Utf8(html),
          },
        ],
      });
      await markStormReportSent(row.id);
      onSent();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t("Email the storm report")}
      subtitle={t("Goes out as a link and as an attached copy.")}
      onClose={onClose}
      actions={
        <>
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="btn-primary"
            // Fire and forget: send() shows its own errors.
            onClick={() => void send()}
            disabled={busy || !to.trim() || !subject.trim()}
          >
            {busy ? t("Sending…") : t("Send")}
          </button>
        </>
      }
    >
      <ErrorNote error={error} />
      <div className="fgrid one">
        <div className="field">
          <label>{t("To")}</label>
          <input
            type="email"
            value={to}
            placeholder="adjuster@example.com"
            onChange={(e) => setTo(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label>{t("Subject")}</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label>{t("Message")}</label>
          <textarea
            rows={8}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>
    </Modal>
  );
}

/**
 * Base64 of a UTF-8 string.
 *
 * `btoa` refuses any character above U+00FF, and the report carries en dashes
 * and a degree-minus sign as a matter of course — so the bytes are made first
 * and encoded second. Straight `btoa(html)` throws on the very first report
 * that has a real address in it.
 */
export function base64Utf8(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}
